import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MarketingNav, MarketingFooter, PipelineBar } from "@/components/marketing/MarketingChrome";
import { USE_CASES, FEATURED, HERO_CHIPS, PRODUCTION_SEQUENCE } from "@/data/useCases";
import { SHOWCASE } from "@/data/showcase";
import { TIERS, priceLabel } from "@/data/pricing";
import { Seo } from "@/components/Seo";

export default function Landing() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const [active, setActive] = useState(FEATURED[0]);

  const begin = () => {
    if (draft.trim()) sessionStorage.setItem("espresso_draft", draft.trim());
    navigate("/register");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Seo
        title="Ingest a Prompt or a 12,000-Row Index — Get an Entire Publication"
        description="Espresso is a knowledge processor: ingest a prompt, index, or knowledge base, structure it into modules and documents, process it at scale with batch pacing, and publish books, courses, research, manuals and more."
        path="/"
      />
      <MarketingNav />

      {/* Hero */}
      <section className="relative overflow-hidden grid-lines border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
          <div className="mb-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="text-primary">Espresso</span>
            <span className="h-px flex-1 bg-border" />
            <span>Knowledge Processor</span>
            <span className="hidden sm:inline">v{new Date().getFullYear()}</span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-title"
            className="max-w-4xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          >
            Ingest a prompt or a 12,000-row index.{" "}
            <span className="text-primary">Publish the whole thing</span>.
          </motion.h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Espresso is a knowledge processor. Feed it a prompt, an index, or an entire knowledge base —
            it decomposes the structure, processes thousands of documents with batch pacing, and synthesises
            publication-ready books, courses, research and manuals. Built for knowledge-ops teams and authors alike.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-xs text-muted-foreground">
            <span><span className="text-foreground font-semibold">10,000+</span> docs / run</span>
            <span><span className="text-foreground font-semibold">Multi-million</span> words</span>
            <span><span className="text-foreground font-semibold">Daily-batch</span> pacing</span>
            <span><span className="text-foreground font-semibold">PDF · EPUB · Word</span> output</span>
          </div>

          {/* Writing box */}
          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-8"
            >
              <div className="border border-border bg-card">
                <div className="flex items-center justify-between border-b border-border px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <span>What do you want to build?</span>
                  <span>job_ref #{String(Math.floor(Math.random() * 900) + 100)}</span>
                </div>
                <textarea
                  data-testid="hero-writing-box"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) begin(); }}
                  rows={4}
                  placeholder="An internal knowledge base for our kitchen operations — 300+ SOPs across prep, service and safety…"
                  className="w-full resize-none bg-transparent px-4 py-4 font-sans text-lg leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/60"
                />
                <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-3">
                  {HERO_CHIPS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setDraft((d) => (d ? d : `A ${c.toLowerCase()} about `))}
                      className="rounded-sm border border-border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
                      data-testid={`hero-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button size="lg" onClick={begin} data-testid="hero-begin-button" className="group rounded-sm px-8">
                  Begin
                  <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                <Link to="/use-cases">
                  <Button size="lg" variant="outline" data-testid="hero-explore-button" className="rounded-sm px-6">
                    Explore what Espresso can make →
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Production sequence */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="lg:col-span-4"
              data-testid="production-sequence"
            >
              <div className="h-full border border-border bg-card p-5">
                <div className="mb-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Processor · Pipeline
                </div>
                <div className="space-y-2">
                  {PRODUCTION_SEQUENCE.map((step, i) => (
                    <div key={step}>
                      <motion.div
                        initial={{ opacity: 0.35 }}
                        animate={{ opacity: [0.35, 1, 0.35] }}
                        transition={{ duration: PRODUCTION_SEQUENCE.length * 0.7, times: undefined, delay: i * 0.7, repeat: Infinity }}
                        className="flex items-baseline gap-3"
                      >
                        <span className="font-mono text-[10px] text-primary">{String(i + 1).padStart(2, "0")}</span>
                        <span className="font-display text-lg tracking-tight">{step}</span>
                      </motion.div>
                      {i < PRODUCTION_SEQUENCE.length - 1 && (
                        <ArrowDown size={12} className="ml-1 my-0.5 text-muted-foreground/50" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Interactive "I want to make…" */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
          <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            I want to produce…
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 border-b border-border pb-5" data-testid="i-want-to-make-selector">
            {FEATURED.map((u) => (
              <button
                key={u.slug}
                onClick={() => setActive(u)}
                data-testid={`make-option-${u.slug}`}
                className={`relative font-serif text-2xl leading-tight tracking-tight transition-colors sm:text-3xl ${
                  active.slug === u.slug ? "text-foreground" : "text-muted-foreground/50 hover:text-muted-foreground"
                }`}
              >
                {u.label}
                {active.slug === u.slug && (
                  <motion.span layoutId="make-underline" className="absolute -bottom-[21px] left-0 h-[2px] w-full bg-primary" />
                )}
              </button>
            ))}
          </div>

          <motion.div
            key={active.slug}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="mt-8"
          >
            <p className="mb-5 max-w-2xl font-display text-xl text-foreground">{active.tagline}</p>
            <PipelineBar steps={active.pipeline} />
            <div className="mt-6">
              <Link to={`/use-cases/${active.slug}`}>
                <Button size="lg" className="group rounded-sm px-8" data-testid="start-making-button">
                  Start making
                  <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use cases preview */}
      <section className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
        <div className="flex items-end justify-between gap-6 border-b border-border pb-6">
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
              What can Espresso produce?
            </h2>
            <p className="mt-2 text-muted-foreground">From a single prompt to an entire body of knowledge — processed and bound.</p>
          </div>
          <Link to="/use-cases" className="hidden sm:block">
            <Button variant="ghost" className="rounded-sm font-mono text-xs uppercase tracking-widest">
              All outputs →
            </Button>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-2 lg:grid-cols-4" data-testid="use-cases-grid">
          {FEATURED.map((u, i) => (
            <Link
              key={u.slug}
              to={`/use-cases/${u.slug}`}
              data-testid="use-case-specimen-card"
              className="group flex flex-col bg-card p-6 transition-colors hover:bg-secondary"
            >
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>{u.ref}</span>
                <span>{u.category.split(" ")[0]}</span>
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold leading-tight tracking-tight">{u.kind}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{u.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-primary">
                View output
                <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Showcase — a real book made with Espresso */}
      <section className="border-t border-border bg-card" data-testid="showcase-banner">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-6 py-16 lg:grid-cols-12 lg:py-20">
          <Link to="/showcase" className="group relative mx-auto max-w-[260px] lg:col-span-4" data-testid="showcase-banner-cover">
            <div className="absolute -inset-3 rounded-md bg-[hsl(var(--matcha)/0.12)] blur-xl transition-opacity group-hover:opacity-80" aria-hidden />
            <img
              src={SHOWCASE.cover}
              alt={`Cover of ${SHOWCASE.title}`}
              className="relative w-full rounded-md border border-border shadow-2xl transition-transform group-hover:-translate-y-1"
            />
          </Link>
          <div className="lg:col-span-8">
            <div className="mb-4 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--matcha))]">
              <span>Made with Espresso</span>
              <span className="h-px w-10 bg-border" />
              <span className="text-muted-foreground">{SHOWCASE.ref}</span>
            </div>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{SHOWCASE.title}</h2>
            <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">{SHOWCASE.subtitle}</p>
            <div className="mt-6 flex flex-wrap gap-6">
              {SHOWCASE.stats.map((st) => (
                <div key={st.label}>
                  <div className="font-display text-2xl font-bold tracking-tight text-[hsl(var(--teal))]">{st.value}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{st.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/showcase" data-testid="showcase-banner-cta">
                <Button className="group rounded-sm px-7">
                  Explore the book
                  <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="border-t border-border" data-testid="pricing-teaser">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:py-20">
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Pricing</div>
              <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                Start free. Scale to enterprise.
              </h2>
            </div>
            <Link to="/pricing" className="hidden sm:block">
              <Button variant="ghost" className="rounded-sm font-mono text-xs uppercase tracking-widest" data-testid="teaser-all-plans">
                Compare all 6 plans →
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {TIERS.filter((t) => ["free", "professional", "business"].includes(t.key)).map((t) => {
              const p = priceLabel(t, "monthly");
              return (
                <Link
                  key={t.key}
                  to="/pricing"
                  data-testid={`teaser-tier-${t.key}`}
                  className={`group flex flex-col rounded-md border bg-card p-6 transition-colors ${
                    t.popular ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]" : "border-border hover:border-primary/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{t.name}</span>
                    {t.popular && (
                      <span className="rounded-sm bg-primary px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-primary-foreground">
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="font-display text-3xl font-bold tracking-tight">{p.amount}</span>
                    {p.sub && <span className="mb-1 font-mono text-xs text-muted-foreground">{p.sub}</span>}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">{t.who}</div>
                  <span className="mt-5 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-primary">
                    View plans <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>


      {/* Proposition CTA */}
      <section className="border-t border-border bg-secondary">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-6 py-16 md:flex-row md:items-center">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Knowledge deserves an engine</div>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Bring your first knowledge base online.
            </h2>
          </div>
          <Link to="/register">
            <Button size="lg" data-testid="cta-bottom-button" className="rounded-sm px-8">
              Begin
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
